import { useAuth } from "@/hooks/use-auth";
import { usePdfSSE } from "@/hooks/usePdfSSE";
import { useSubscriptionGuard } from "@/hooks/use-subscription-guard";
import { useLocation } from "wouter";
import { useEffect } from "react";
import logo from "@/assets/turboclaim-logo.png";

type ProtectedRouteProps = {
    children: React.ReactNode;
    skipSubscriptionCheck?: boolean;
};

export default function ProtectedRoute({ children, skipSubscriptionCheck = false }: ProtectedRouteProps) {
    const { user, isLoading: isAuthLoading } = useAuth();
    const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscriptionGuard({ 
        skipRedirect: skipSubscriptionCheck 
    });
    const [, setLocation] = useLocation();

    // Listen for PDF export completion across all supplements
    // This persists across navigation so users can navigate away while PDFs generate
    usePdfSSE();

    useEffect(() => {
        if (!isAuthLoading && !user) {
            setLocation("/login");
        }
    }, [user, isAuthLoading, setLocation]);

    // Show loading state while checking auth (and subscription if not skipped)
    const isLoading = isAuthLoading || (!skipSubscriptionCheck && isSubscriptionLoading);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center">
                    <div className="animate-pulse">
                        <img src={logo} alt="TurboClaim" className="h-12 mx-auto mb-4 opacity-60" />
                    </div>
                    <p className="text-slate-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return null; // Should redirect via useEffect
    }

    // Subscription guard will redirect if no active subscription (unless skipped)
    if (!skipSubscriptionCheck && !hasActiveSubscription) {
        return null;
    }

    return <>{children}</>;
}
